import "phaser";

import { FLOAT_EPSILON, MIN_VISIBLE } from "./constants";
import * as logging from "./logging";


/**
 * The slope of a line, represented as (normalized) x and y deltas.
 */
export interface Slope {
    dx: number,
    dy: number,
}

export interface PointLike {
    x: number,
    y: number,
}

export interface NormalFormLine {
    a: number,
    b: number,
    c: number,
}

export interface AABBLike {
    x: number,
    y: number,
    width: number,
    height: number,
}

export type LineSide = "intersects" | "left" | "right";
export type ConstraintDirection = "left" | "right" | "top" | "bottom";

export class Line {
    point: PointLike;
    slope: Slope;

    constructor(point: PointLike, slope: Slope) {
        this.point = point;
        this.slope = slope;
    }

    normalForm(): NormalFormLine {
        const A = -this.slope.dy;
        const B = this.slope.dx;
        const C = this.slope.dy * this.point.x - this.slope.dx * this.point.y;
        return {
            a: A,
            b: B,
            c: C,
        };
    }

    intersectsAABB(aabb: AABBLike): boolean {
        // 1. We write the equation of the line as Ax + By + C = 0.
        // 2. We substitute the four points defining the AABB into
        //    this equation, and take the signs.
        // 3. If the signs are all positive or all negative, there is
        //    no intersection.
        // 4. Else, the line intersects.

        // Reasoning: the line divides the plane into two regions for
        // which f(x, y) = Ax + By + C > 0 and f(x, y) < 0. For the
        // AABB to intersect, the signs of two of the points of the
        // rectangle corners must differ.
        const { a: A, b: B, c: C } = this.normalForm();

        const s1 = A*aabb.x + B*aabb.y + C;
        const s2 = A*(aabb.x + aabb.width) +
            B*aabb.y + C;
        const s3 = A*aabb.x +
            B*(aabb.y + aabb.height) + C;
        const s4 = A*(aabb.x + aabb.width) +
            B*(aabb.y + aabb.height) + C;

        if ((s1 > 0 && s2 > 0 && s3 > 0 && s4 > 0) ||
            (s1 < 0 && s2 < 0 && s3 < 0 && s4 < 0)) {
            return false;
        }

        return true;
    }

    intersectsAABBTime(aabb: AABBLike): number | null {
        return null;
    }

    intersectsLine(other: Line): false | [number, number] {
        // https://www.topcoder.com/community/data-science/data-science-tutorials/geometry-concepts-line-intersection-and-its-applications/
        const line1 = this.normalForm();
        const line2 = other.normalForm();
        const det = line1.a * line2.b - line2.a * line1.b;
        if (Math.abs(det) < FLOAT_EPSILON) {
            // Lines are parallel
            return false;
        }
        return [
            (line1.b * line2.c - line2.b * line1.c) / det,
            (line2.a * line1.c - line1.a * line2.c) / det,
        ];
    }

    intersectsRay(ray: Ray): boolean {
        const lineLine = this.intersectsLine(ray);
        if (lineLine === false) return false;

        const [x, y] = lineLine;
        const rayDirection = ray.slope;
        if (Math.abs(rayDirection.dx) >= FLOAT_EPSILON) {
            const t = (x - ray.point.x) / rayDirection.dx;
            return t >= 0;
        }
        else if (Math.abs(rayDirection.dy) >= FLOAT_EPSILON) {
            const t = (y - ray.point.y) / rayDirection.dy;
            return t >= 0;
        }
        else {
            throw "Ray is actually a point, thanks.";
        }
    }

    isVertical(): boolean {
        return Math.abs(this.slope.dx) < FLOAT_EPSILON;
    }

    isHorizontal(): boolean {
        return Math.abs(this.slope.dy) < FLOAT_EPSILON;
    }

    /**
     * On which side of the line lies the point queryPoint?
     */
    whichSide(queryPoint: PointLike): LineSide {
        // https://stackoverflow.com/a/1560510/262727
        const A = this.point;
        const B = {
            x: this.point.x + this.slope.dx,
            y: this.point.y + this.slope.dy
        };
        const position = (B.x - A.x) * (queryPoint.y - A.y) -
            (B.y - A.y) * (queryPoint.x - A.x);
        if (position < 0) {
            return "left";
        }
        else if (position > 0) {
            return "right";
        }
        else {
            return "intersects";
        }

    }
}

export class Ray extends Line {
    intersectsAABBTime(aabb: AABBLike): number | null {
        // https://www.scratchapixel.com/lessons/3d-basic-rendering/minimal-ray-tracer-rendering-simple-shapes/ray-box-intersection

        let tmin = (aabb.x - this.point.x) / this.slope.dx;
        let tmax = (aabb.x + aabb.width - this.point.x) / this.slope.dx;

        if (tmin > tmax) [tmin, tmax] = [tmax, tmin];

        let tymin = (aabb.y - this.point.y) / this.slope.dy;
        let tymax = (aabb.y + aabb.height - this.point.y) / this.slope.dy;

        if (tymin > tymax) [tymin, tymax] = [tymax, tymin];

        if ((tmin > tymax) || (tymin > tmax))
            return null;
        if (tymin > tmin)
            tmin = tymin;
        if (tymax < tmax)
            tmax = tymax;

        if (tmin < 0) {
            if (tmax > 0) {
                return 0;
            }
            return null;
        }
        return tmin;
    }

    intersectsAABB(aabb: AABBLike): boolean {
        const time = this.intersectsAABBTime(aabb);
        return time !== null;
    }
}

export function signOf(x: number) {
    if (x > 0) {
        return 1;
    }
    else if (x < 0) {
        return -1;
    }
    else {
        return 0;
    }
}

function visiblePart(coord: number, dim: number, levelDimMin: number, levelDim: number): number {
    if (coord < levelDimMin) return dim + coord - levelDimMin;
    else if (coord + dim > levelDimMin + levelDim) return levelDimMin + levelDim - coord;
    else return dim;
}

export function constrainAABBTranslation(
    aabb: AABBLike,
    dx: number, dy: number,
    bbox: AABBLike,
): [number, ConstraintDirection[]] {
    const x = aabb.x + dx;
    const y = aabb.y + dy;
    const directions: ConstraintDirection[] = [];

    if (logging.CONDITION === logging.ABCondition.Offscreen) {
        // Proportion of the proposed translation that can be used
        let xOvershoot = 1;
        let yOvershoot = 1;

        const beforeVisibleWidth  = visiblePart(aabb.x,      aabb.width,  bbox.x, bbox.width);
        const afterVisibleWidth   = visiblePart(aabb.x + dx, aabb.width,  bbox.x, bbox.width);
        const beforeVisibleHeight = visiblePart(aabb.y,      aabb.height, bbox.y, bbox.height);
        const afterVisibleHeight  = visiblePart(aabb.y + dy, aabb.height, bbox.y, bbox.height);

        const offscreenX = afterVisibleWidth < aabb.width;
        const offscreenY = afterVisibleHeight < aabb.height;
        if (beforeVisibleWidth <= MIN_VISIBLE &&
            afterVisibleWidth < beforeVisibleWidth) {
            directions.push(dx > 0 ? "right" : "left");
        }
        if (beforeVisibleHeight <= MIN_VISIBLE &&
            afterVisibleHeight < beforeVisibleHeight) {
            directions.push(dy > 0 ? "bottom" : "top");
        }
        if (directions.length > 0)
            return [0, directions];

        if (offscreenX && afterVisibleWidth < MIN_VISIBLE) {
            if (beforeVisibleWidth < MIN_VISIBLE) xOvershoot = 0;
            else xOvershoot = Math.abs((beforeVisibleWidth - MIN_VISIBLE) / dx);
            directions.push(dx > 0 ? "right" : "left");
        }
        if (offscreenY && afterVisibleHeight < MIN_VISIBLE) {
            if (beforeVisibleHeight < MIN_VISIBLE) yOvershoot = 0;
            else yOvershoot = Math.abs((beforeVisibleHeight - MIN_VISIBLE) / dy);
            directions.push(dy > 0 ? "bottom" : "top");
        }
        if (directions.length > 0)
            return [Math.min(xOvershoot, yOvershoot), directions];
    }
    else {
        let scale = 1;
        const offscreenX = x < bbox.x || x + aabb.width > bbox.x + bbox.width;
        const offscreenY = y < bbox.y || y + aabb.height > bbox.y + bbox.height;
        if (offscreenX) {
            if (dx < 0 && aabb.x > bbox.x) {
                scale = Math.min(scale, Math.abs((aabb.x - bbox.x) / dx));
                directions.push("left");
            }
            else if (dx > 0 && aabb.x + aabb.width < bbox.x + bbox.width) {
                scale = Math.min(scale, Math.abs((bbox.x + bbox.width - aabb.x - aabb.width) / dx));
                directions.push("right");
            }
            else {
                scale = 0;
                directions.push(dx > 0 ? "right" : "left")
            }
        }
        if (offscreenY) {
            if (dy < 0 && aabb.y > bbox.y) {
                scale = Math.min(scale, Math.abs((aabb.y - bbox.y) / dy));
                directions.push("top");
            }
            else if (dy > 0 && aabb.y + aabb.height < bbox.y + bbox.height) {
                scale = Math.min(scale, Math.abs((bbox.y + bbox.height - aabb.y - aabb.height) / dy));
                directions.push("bottom");
            }
            else {
                scale = 0;
                directions.push(dy > 0 ? "bottom" : "top")
            }
        }
        if (directions.length > 0)
            return [scale, directions];
    }

    return [1, []];
}



// WEBPACK FOOTER //
// ./src/collision.ts